/* ==========================================================================
   ZAMU AFRICA — Core behaviour
   Vanilla JS, no dependencies. Loaded with `defer`, so it runs after parse
   and before cart.js / product.js, which reuse the helpers on window.ZAMU.

   Security notes:
   - Nothing here writes untrusted strings into the DOM. HTML that comes back
     from Shopify's Section Rendering API is parsed with DOMParser (which does
     not execute scripts) and inserted as nodes, never via innerHTML.
   - Values read from the URL are used only as URLSearchParams data or passed
     to history.replaceState with a same-origin path, never assigned to
     location/href directly.
   ========================================================================== */

(function () {
  'use strict';

  var html = document.documentElement;
  html.classList.add('js');

  /* ------------------------------------------------------------------------
     Small helpers
     ------------------------------------------------------------------------ */

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function focusableWithin(container) {
    return Array.prototype.filter.call(
      container.querySelectorAll(FOCUSABLE),
      function (el) {
        return el.offsetParent !== null || el === document.activeElement;
      }
    );
  }

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      var context = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Formats a price in cents using the shop's money format.
   * Falls back to a plain amount if the format string is unavailable.
   */
  function formatMoney(cents) {
    var amount = (Number(cents) / 100).toFixed(2);
    var format = window.ZAMU && window.ZAMU.moneyFormat;
    if (!format) return amount;
    // Shopify money formats use {{amount}} style placeholders.
    return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
  }

  /* ------------------------------------------------------------------------
     Scroll lock — preserves scroll position and avoids the scrollbar jump
     ------------------------------------------------------------------------ */

  var lockCount = 0;
  var lockedScrollY = 0;

  function lockScroll() {
    if (lockCount === 0) {
      var scrollbar = window.innerWidth - html.clientWidth;
      html.style.setProperty('--scrollbar-width', scrollbar + 'px');
      lockedScrollY = window.scrollY;
      document.body.classList.add('is-locked');
    }
    lockCount += 1;
  }

  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
      document.body.classList.remove('is-locked');
      html.style.removeProperty('--scrollbar-width');
      window.scrollTo(0, lockedScrollY);
    }
  }

  /* ------------------------------------------------------------------------
     Focus trap
     ------------------------------------------------------------------------ */

  function FocusTrap(container) {
    this.container = container;
    this.previouslyFocused = null;
    this.onKeydown = this.onKeydown.bind(this);
  }

  FocusTrap.prototype.activate = function (initialFocus) {
    this.previouslyFocused = document.activeElement;
    document.addEventListener('keydown', this.onKeydown);

    var container = this.container;
    var attempts = 0;

    // Panels animate in from `visibility: hidden`, and focus() is a silent
    // no-op while an element is invisible — a single rAF is not enough,
    // because at transition progress 0 visibility still computes as hidden.
    // Wait for it to actually become visible, with a bounded retry so a
    // panel that never opens can't spin forever.
    (function focusWhenVisible() {
      if (window.getComputedStyle(container).visibility === 'visible') {
        var target = initialFocus || focusableWithin(container)[0] || container;
        if (target && typeof target.focus === 'function') {
          target.focus({ preventScroll: true });
        }
        return;
      }

      attempts += 1;
      if (attempts > 30) return;
      window.requestAnimationFrame(focusWhenVisible);
    })();
  };

  FocusTrap.prototype.deactivate = function () {
    document.removeEventListener('keydown', this.onKeydown);
    if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
      this.previouslyFocused.focus({ preventScroll: true });
    }
    this.previouslyFocused = null;
  };

  FocusTrap.prototype.onKeydown = function (event) {
    if (event.key !== 'Tab') return;

    var items = focusableWithin(this.container);
    if (items.length === 0) {
      event.preventDefault();
      return;
    }

    var first = items[0];
    var last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* ------------------------------------------------------------------------
     Panel controller — shared by the mobile nav, search panel and cart drawer
     ------------------------------------------------------------------------ */

  var openPanels = [];

  function Panel(element, options) {
    options = options || {};
    this.element = element;
    this.overlay = options.overlay || document.querySelector('[data-overlay]');
    this.openClass = options.openClass || 'is-open';
    this.initialFocusSelector = options.initialFocusSelector || null;
    this.trap = new FocusTrap(element);
    this.isOpen = false;
    this.triggers = [];

    this.onOverlayClick = this.close.bind(this);
    this.onEscape = this.onEscape.bind(this);

    var self = this;
    Array.prototype.forEach.call(
      element.querySelectorAll('[data-panel-close]'),
      function (button) {
        button.addEventListener('click', function () {
          self.close();
        });
      }
    );
  }

  Panel.prototype.registerTrigger = function (trigger) {
    var self = this;
    this.triggers.push(trigger);
    trigger.setAttribute('aria-expanded', 'false');
    trigger.addEventListener('click', function (event) {
      event.preventDefault();
      self.toggle(trigger);
    });
  };

  Panel.prototype.toggle = function (trigger) {
    if (this.isOpen) {
      this.close();
    } else {
      this.open(trigger);
    }
  };

  Panel.prototype.open = function () {
    if (this.isOpen) return;

    // Only one panel at a time keeps focus management predictable.
    openPanels.slice().forEach(function (panel) {
      panel.close();
    });

    this.isOpen = true;
    this.element.classList.add(this.openClass);
    this.element.removeAttribute('aria-hidden');

    if (this.overlay) {
      this.overlay.classList.add('is-active');
      this.overlay.addEventListener('click', this.onOverlayClick);
    }

    this.triggers.forEach(function (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
    });

    lockScroll();
    document.addEventListener('keydown', this.onEscape);
    openPanels.push(this);

    var initial = this.initialFocusSelector
      ? this.element.querySelector(this.initialFocusSelector)
      : null;
    this.trap.activate(initial);

    this.element.dispatchEvent(new CustomEvent('panel:open', { bubbles: true }));
  };

  Panel.prototype.close = function () {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.element.classList.remove(this.openClass);
    this.element.setAttribute('aria-hidden', 'true');

    if (this.overlay) {
      this.overlay.classList.remove('is-active');
      this.overlay.removeEventListener('click', this.onOverlayClick);
    }

    this.triggers.forEach(function (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
    });

    document.removeEventListener('keydown', this.onEscape);
    openPanels = openPanels.filter(function (panel) {
      return panel !== this;
    }, this);

    this.trap.deactivate();
    unlockScroll();

    this.element.dispatchEvent(new CustomEvent('panel:close', { bubbles: true }));
  };

  Panel.prototype.onEscape = function (event) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      this.close();
    }
  };

  /* ------------------------------------------------------------------------
     Reveal on scroll
     ------------------------------------------------------------------------ */

  function initReveal(root) {
    var targets = (root || document).querySelectorAll('[data-reveal]:not(.is-revealed)');
    if (targets.length === 0) return;

    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(targets, function (el) {
        el.classList.add('is-revealed');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    Array.prototype.forEach.call(targets, function (el, index) {
      // Stagger siblings within a group so grids cascade rather than pop.
      if (!el.style.getPropertyValue('--reveal-delay')) {
        el.style.setProperty('--reveal-delay', Math.min(index, 6) * 70 + 'ms');
      }
      observer.observe(el);
    });
  }

  /* ------------------------------------------------------------------------
     Header — publishes its measured height

     The header is sticky via CSS on its section wrapper. This only measures
     how tall it is so anything sticky further down the page (the catalog
     toolbar, the product column) can offset by the right amount. It must
     never feed back into the header's own sizing.
     ------------------------------------------------------------------------ */

  function initHeader() {
    var wrapper = document.querySelector('[data-header-wrapper]');
    if (!wrapper) return;

    var header = wrapper.querySelector('.header');
    // The sticky element is the section wrapper, so measure that when present.
    var sticky = wrapper.closest('.section--header') || wrapper;

    function syncOffset() {
      var height = sticky.offsetHeight;
      if (height) {
        html.style.setProperty('--header-offset', height + 'px');
      }
    }

    function syncStuck() {
      // Once the announcement bar has scrolled past, the header is pinned.
      var pinned = sticky.getBoundingClientRect().top <= 0;
      sticky.classList.toggle('is-stuck', pinned);
    }

    syncOffset();
    syncStuck();

    window.addEventListener('scroll', syncStuck, { passive: true });
    window.addEventListener('resize', debounce(syncOffset, 150));

    if ('ResizeObserver' in window && header) {
      new ResizeObserver(syncOffset).observe(header);
    }
  }

  /* ------------------------------------------------------------------------
     Navigation panels (mobile menu + search)
     ------------------------------------------------------------------------ */

  function initNavigation() {
    var overlay = document.querySelector('[data-overlay]');

    var menu = document.querySelector('[data-mobile-nav]');
    if (menu) {
      var menuPanel = new Panel(menu, { overlay: overlay });
      Array.prototype.forEach.call(
        document.querySelectorAll('[data-menu-toggle]'),
        function (trigger) {
          menuPanel.registerTrigger(trigger);
        }
      );
      // Navigating away should not leave the drawer open on back-navigation.
      Array.prototype.forEach.call(menu.querySelectorAll('a[href]'), function (link) {
        link.addEventListener('click', function () {
          menuPanel.close();
        });
      });
      window.ZAMU.menuPanel = menuPanel;
    }

    var search = document.querySelector('[data-search-panel]');
    if (search) {
      var searchPanel = new Panel(search, {
        overlay: overlay,
        initialFocusSelector: 'input[type="search"]'
      });
      Array.prototype.forEach.call(
        document.querySelectorAll('[data-search-toggle]'),
        function (trigger) {
          searchPanel.registerTrigger(trigger);
        }
      );
      window.ZAMU.searchPanel = searchPanel;
    }
  }

  /* ------------------------------------------------------------------------
     Announcement bar rotation

     Slides cross-fade upward: the outgoing message continues up and out while
     the next rises into its place.

     Accessibility notes:
     - Rotation pauses on hover and whenever focus enters the bar, and stops
       while the tab is hidden, so it never animates unseen.
     - With prefers-reduced-motion the bar does not auto-rotate at all. The
       previous/next arrows keep every message reachable, which is also what
       satisfies WCAG 2.2.2 for auto-updating content.
     - Inactive slides are aria-hidden so a screen reader reads one message,
       not all of them at once.
     ------------------------------------------------------------------------ */

  function AnnouncementBar(root) {
    this.root = root;
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-announcement-slide]'));
    if (this.slides.length < 2) return;

    this.index = Math.max(0, this.slides.findIndex(function (s) {
      return s.classList.contains('is-active');
    }));
    this.interval = Math.max(2000, Number(root.getAttribute('data-interval')) || 5000);
    this.timer = null;
    this.paused = false;

    var self = this;
    var container = root.closest('.announcement') || root;

    Array.prototype.forEach.call(
      container.querySelectorAll('[data-announcement-nav]'),
      function (button) {
        button.addEventListener('click', function () {
          var step = button.getAttribute('data-announcement-nav') === 'next' ? 1 : -1;
          self.show(self.index + step);
          self.restart();
        });
      }
    );

    ['mouseenter', 'focusin'].forEach(function (evt) {
      container.addEventListener(evt, function () {
        self.paused = true;
        self.stop();
      });
    });

    ['mouseleave', 'focusout'].forEach(function (evt) {
      container.addEventListener(evt, function () {
        // Don't resume while focus is still somewhere inside the bar.
        if (evt === 'focusout' && container.contains(document.activeElement)) return;
        self.paused = false;
        self.start();
      });
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) self.stop();
      else if (!self.paused) self.start();
    });

    // Selecting a block in the theme editor should bring that slide forward.
    document.addEventListener('shopify:block:select', function (event) {
      var slide = event.target.closest
        ? event.target.closest('[data-announcement-slide]')
        : null;
      if (!slide || self.slides.indexOf(slide) === -1) return;
      self.stop();
      self.show(self.slides.indexOf(slide));
    });

    document.addEventListener('shopify:block:deselect', function () {
      if (!self.paused) self.start();
    });

    this.start();
  }

  AnnouncementBar.prototype.show = function (next) {
    var count = this.slides.length;
    var target = ((next % count) + count) % count;
    if (target === this.index) return;

    var outgoing = this.slides[this.index];
    var incoming = this.slides[target];

    outgoing.classList.remove('is-active');
    outgoing.classList.add('is-leaving');
    outgoing.setAttribute('aria-hidden', 'true');

    incoming.classList.remove('is-leaving');
    incoming.classList.add('is-active');
    incoming.removeAttribute('aria-hidden');

    // Clear the leaving state once it has travelled, so it can re-enter later.
    window.setTimeout(function () {
      outgoing.classList.remove('is-leaving');
    }, 700);

    this.index = target;
  };

  AnnouncementBar.prototype.start = function () {
    // Auto-advancing motion is suppressed entirely for reduced-motion users.
    if (prefersReducedMotion() || this.timer) return;

    var self = this;
    this.timer = window.setInterval(function () {
      self.show(self.index + 1);
    }, this.interval);
  };

  AnnouncementBar.prototype.stop = function () {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  };

  AnnouncementBar.prototype.restart = function () {
    this.stop();
    if (!this.paused) this.start();
  };

  function initAnnouncements(scope) {
    Array.prototype.forEach.call(
      (scope || document).querySelectorAll('[data-announcement]'),
      function (root) {
        if (root.dataset.announcementBound === 'true') return;
        root.dataset.announcementBound = 'true';
        new AnnouncementBar(root);
      }
    );
  }

  /* ------------------------------------------------------------------------
     Public namespace
     ------------------------------------------------------------------------ */

  window.ZAMU = window.ZAMU || {};
  window.ZAMU.Panel = Panel;
  window.ZAMU.FocusTrap = FocusTrap;
  window.ZAMU.initReveal = initReveal;
  window.ZAMU.AnnouncementBar = AnnouncementBar;
  window.ZAMU.formatMoney = formatMoney;
  window.ZAMU.debounce = debounce;
  window.ZAMU.prefersReducedMotion = prefersReducedMotion;
  window.ZAMU.lockScroll = lockScroll;
  window.ZAMU.unlockScroll = unlockScroll;

  /* ------------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------------ */

  function init() {
    initHeader();
    initNavigation();
    initAnnouncements(document);
    initReveal(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run for sections the Theme Editor injects or re-renders.
  document.addEventListener('shopify:section:load', function (event) {
    initHeader();
    initNavigation();
    initAnnouncements(event.target);
    initReveal(event.target);
  });
})();
